import * as React from 'react';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { canDeleteNode } from '../../../BlogEditor/plugins/shared-transforms';
import { MediaProps, MediaType } from '../media-constant';
import { DEFI, EX, IMG, LEFT, PRONOUNCE, RIGHT, RIGHT1, WORD } from './type';
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';

const VocabNodeEditable = dynamicEditorComponent(() =>
  import(/* webpackExports: "VocabNodeEditable" */ './VocabCardNode').then(
    (mod) => mod.VocabNodeEditable
  )
);
const ImageUploadEditable = dynamicEditorComponent(
  () =>
    import(/* webpackExports: "VocabNodeEditable" */ './child-node/right-hand-node').then(
      (mod) => mod.ImageUploadEditable
    ),
  { dependsOn: VocabNodeEditable }
);

const LeffHand = dynamicEditorComponent<any>(
  () =>
    import(/* webpackExports: "LeffHand" */ './child-node/left-hand-node').then(
      (mod) => mod.LeffHand
    ),
  {
    ssr: true,
  }
);
const Pronounce = dynamicEditorComponent<any>(
  () =>
    import(/* webpackExports: "Pronounce" */ './child-node/left-hand-node').then(
      (mod) => mod.Pronounce
    ),
  {
    ssr: true,
  }
);
const Word = dynamicEditorComponent<any>(
  () => import(/* webpackExports: "Word" */ './child-node/left-hand-node').then((mod) => mod.Word),
  {
    ssr: true,
  }
);

const Definition = dynamicEditorComponent<any>(
  () =>
    import(/* webpackExports: "Definition" */ './child-node/right-hand-node').then(
      (mod) => mod.Definition
    ),
  {
    ssr: true,
  }
);
const Example = dynamicEditorComponent<any>(
  () =>
    import(/* webpackExports: "Example" */ './child-node/right-hand-node').then(
      (mod) => mod.Example
    ),
  {
    ssr: true,
  }
);

const Right1 = dynamicEditorComponent<any>(
  () =>
    import(/* webpackExports: "Right1" */ './child-node/right-hand-node').then((mod) => mod.Right1),
  {
    ssr: true,
  }
);

const RightHand = dynamicEditorComponent<any>(
  () =>
    import(/* webpackExports: "RightHand" */ './child-node/right-hand-node').then(
      (mod) => mod.RightHand
    ),
  {
    ssr: true,
  }
);

export const renderVocabCard = (opt: MediaProps) => (props: RenderElementProps) => {
  const { element } = props;
  const { isPreview } = opt;
  const options = { isPreview: isPreview || element.isPreview };
  switch (element.type) {
    case MediaType.VOCAB_CARD: {
      return (
        <div id={element.id as string}>
          <VocabNodeEditable id={element.id as string} {...props} {...options} />
        </div>
      );
    }
    case LEFT:
      return <LeffHand {...props} {...options} />;
    case WORD:
      return <Word {...props} {...options} />;
    case PRONOUNCE:
      return <Pronounce {...props} {...options} />;
    case RIGHT:
      return <RightHand {...props} {...options} />;
    case RIGHT1:
      return <Right1 {...props} {...options} />;
    case DEFI:
      return <Definition {...props} {...options} />;
    case EX:
      return <Example {...props} {...options} />;
    case IMG:
      return <ImageUploadEditable {...props} {...options} />;
  }
  return null;
};

export const withVocabCardPlugin =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { deleteBackward, deleteForward } = editor;

    const matchNode = (node) => [WORD, PRONOUNCE, EX, DEFI, IMG].includes(node?.type as any);

    editor.deleteBackward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'backward',
        match: matchNode,
      });
      if (!canDelete) {
        return;
      }
      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'forward',
        match: matchNode,
      });
      if (!canDelete) {
        return;
      }
      deleteForward(unit);
    };
    return editor;
  };
